import type { RoomDetail, RoomTheme } from '@crowdset/contracts';
import { API, ROOM_THEMES } from '@crowdset/contracts/constants';
import { Backdrop, THEME_META } from '@crowdset/ui';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useNavigate } from 'react-router';
import { api, ApiError, errorMessage } from '../lib/api';
import { Button, cx, Dialog, Field, Input, Textarea } from '../ui/kit';

export function CreateRoomDialog({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (o: boolean) => void;
}) {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [visibility, setVisibility] = useState<'public' | 'private'>('public');
  const [theme, setTheme] = useState<RoomTheme>('neon-club');
  const [tags, setTags] = useState('');
  const nav = useNavigate();
  const qc = useQueryClient();
  const create = useMutation({
    mutationFn: () =>
      api.post<RoomDetail>(API.rooms, {
        name,
        description,
        visibility,
        theme,
        genreTags: tags
          .split(',')
          .map((t) => t.trim().toLowerCase())
          .filter(Boolean)
          .slice(0, 5),
      }),
    onSuccess: (room) => {
      void qc.invalidateQueries({ queryKey: ['rooms'] });
      onOpenChange(false);
      nav(`/r/${room.slug}`);
    },
  });
  const fieldErr = (k: string) =>
    create.error instanceof ApiError &&
    create.error.code === 'VALIDATION' &&
    create.error.details?.[k]
      ? String(create.error.details[k])
      : null;

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      title="Create a room"
      description="Rooms start quiet. Invite people or join the queue yourself."
      wide
    >
      <form
        className="flex flex-col gap-5"
        onSubmit={(e) => {
          e.preventDefault();
          create.mutate();
        }}
      >
        <Field label="Room name" error={fieldErr('name')}>
          {(id, d) => (
            <Input
              id={id}
              aria-describedby={d}
              required
              minLength={3}
              maxLength={60}
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Basement Frequencies"
            />
          )}
        </Field>
        <Field label="Description" hint="Optional. Up to 280 characters.">
          {(id, d) => (
            <Textarea
              id={id}
              aria-describedby={d}
              maxLength={280}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
          )}
        </Field>
        <Field label="Genre tags" hint="Comma separated, up to 5 (e.g. house, disco).">
          {(id, d) => (
            <Input
              id={id}
              aria-describedby={d}
              value={tags}
              onChange={(e) => setTags(e.target.value)}
              placeholder="house, disco"
            />
          )}
        </Field>
        <fieldset className="flex flex-col gap-2">
          <legend className="mb-2 text-[13px] font-medium">Theme</legend>
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
            {ROOM_THEMES.map((t) => (
              <label
                key={t}
                className={cx(
                  'relative cursor-pointer overflow-hidden rounded-xl border transition-colors has-[:focus-visible]:ring-2 has-[:focus-visible]:ring-accent',
                  theme === t ? 'border-accent/70' : 'border-white/10 hover:border-white/25',
                )}
              >
                <input
                  type="radio"
                  name="theme"
                  value={t}
                  checked={theme === t}
                  onChange={() => setTheme(t)}
                  className="sr-only"
                />
                <Backdrop theme={t} className="aspect-[16/8] w-full" />
                <span className="block bg-ink-900 px-3 py-2 text-[13px]">{THEME_META[t].name}</span>
              </label>
            ))}
          </div>
        </fieldset>
        <fieldset>
          <legend className="mb-2 text-[13px] font-medium">Who can join</legend>
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
            {(
              [
                ['public', 'Public', 'Listed in discovery. Anyone can listen.'],
                ['private', 'Private', 'Hidden. Members join with an expiring invite link.'],
              ] as const
            ).map(([v, label, body]) => (
              <label
                key={v}
                className={cx(
                  'cursor-pointer rounded-xl border p-3 transition-colors has-[:focus-visible]:ring-2 has-[:focus-visible]:ring-accent',
                  visibility === v
                    ? 'border-accent/70 bg-accent/[0.06]'
                    : 'border-white/10 hover:border-white/25',
                )}
              >
                <input
                  type="radio"
                  name="visibility"
                  value={v}
                  checked={visibility === v}
                  onChange={() => setVisibility(v)}
                  className="sr-only"
                />
                <span className="block text-sm font-medium">{label}</span>
                <span className="mt-0.5 block text-xs text-fg-muted">{body}</span>
              </label>
            ))}
          </div>
        </fieldset>
        {create.error && !fieldErr('name') && (
          <p role="alert" className="text-sm text-[#f3a3a4]">
            {errorMessage(create.error)}
          </p>
        )}
        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button type="submit" variant="primary" loading={create.isPending}>
            Create room
          </Button>
        </div>
      </form>
    </Dialog>
  );
}
